import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Legend from '@molecules/pursuitLeader/components/legend';
import {
  RowStyle,
  LabelStyle,
  LegendTextStyle,
  InvisibleDiv,
  LegendFutureContainer,
  NoWrapSpan,
} from '@atoms/sidebar/sidebar.styles';
import DropDownSelect from '@atoms/dropDownSelect/dropDownSelect';
import { CurrentPill, FuturePillDashboard } from '@constants/pursuitLeaderConstants';
import {
  QuarterDetailsContainer,
  MonthlyTitleContainer,
  StyledHeader,
  ContainerWrapper,
  DropDownSubHeaderContainer,
  DropDownButtonSubHeaderContainer,
  ViewForecastQuarterButton,
  QuarterHeader,
  PillStyle,
  CommonSubHeaderContainer,
  CommonTitleContainer,
} from '@atoms/dashboard/quarterlyDetails.styles';
import flagIcon from '@assets/icons/forecastIconBlack.svg';
import { MotifButton } from '@ey-xd/motif-react';
import { useRecoilValue } from 'recoil';

import {
  Recoil_Quarter_State,
  Year_Wise_Data_State,
  Current_User,
  Recoil_Selected_Account_State,
  System_Date_Time_State,
} from '@recoil/recoil';
import { formatNumberWithKAndM, trimText } from '@util/commonUtil';
import {
  BUTTON_TITLE,
  MONTH_QUARTER_STATUS,
  QUARTERLY_DETAILS,
  ROLE_CONST,
  QUARTER_OPTIONS_LIST,
  SERVICE_OPTIONS_LIST,
} from '@constants/commonConstants';
import {
  getDashboardAllQuartersData,
  getDashboardSingleQuartersData,
} from '@services/commonServices';
import MonthlyDetails from '@molecules/gcsp/components/dashboard/montlyDetails';
import { logError } from '@util/commonFunctions';

const QuarterDetailsGcsp = () => {
  const navigate = useNavigate();
  const [quarterYearRendering, setQuarterYearRendering] = useState(null);
  const [completeApiResponse, setCompleteApiResponse] = useState(null); // Store complete API response
  const yearQuaterWiseData = useRecoilValue(Year_Wise_Data_State);
  const quarterInfo = useRecoilValue(Recoil_Quarter_State);
  const currentUser = useRecoilValue(Current_User);
  const selectedUser = useRecoilValue(Recoil_Selected_Account_State);
  const systemDateTime = useRecoilValue(System_Date_Time_State);

  let currentYear;
  let quarterYearHeaderList;
  const yearOptionsList = [];

  const buttonStyle = {
    width: 160,
    height: 44,
    fontWeight: 400,
  };

  const buttonStyleForecast = {
    cursor: 'pointer',
    width: 160,
    height: 44,
    fontWeight: 400,
  };

  if (quarterInfo !== null) {
    currentYear = quarterInfo.fiscal_year;
    // currentYear = 2030;

    for (let i = currentYear - 4; i <= parseInt(currentYear, 10) + 1; i++) {
      // Dropdown Conditions
      // 1. Max five years and miniumum two years are allowed.
      // 2. List does not have year less than 2024.
      // 3. Max value permitted is current year + 1.
      // i.e.
      // 2024:- 2024, 2025
      // 2025:- 2024, 2025, 2026
      // 2026:- 2024, 2025, 2026, 2027
      // 2027:- 2024, 2025, 2026, 2027, 2028
      // 2028:- 2025, 2026, 2027, 2028, 2029
      if (i >= 2024 && i > parseInt(currentYear, 10) - 4) {
        yearOptionsList.push({ value: i.toString(), label: i.toString() });
      }
    }

    quarterYearHeaderList = QUARTER_OPTIONS_LIST.map((quarter, index) => ({
      id: index,
      quarterText: quarter.label,
      yearText: currentYear,
      serviceLineText: SERVICE_OPTIONS_LIST[0].value,
      data: null,
    }));
  }

  useEffect(() => {
    if (quarterInfo !== null && selectedUser !== null) {
      if (quarterYearRendering === null) {
        setQuarterYearRendering(quarterYearHeaderList);
      }

      if (Object.values(yearQuaterWiseData).length === 0) {
        const fetchData = async () => {
          try {
            const dashboardAllQuarterData = await getDashboardAllQuartersData(
              currentUser.email,
              currentYear,
              systemDateTime.split('T')[0],
              ROLE_CONST.GCSP,
              selectedUser.account_id,
            );
            setCompleteApiResponse(dashboardAllQuarterData); // Store complete API response
            modifyQuarterYearRendering(dashboardAllQuarterData);
          } catch (error) {
            logError('getDashboardAllQuartersData', error);
          }
        };
        fetchData();
      } else {
        setCompleteApiResponse(yearQuaterWiseData); // Store complete API response
        modifyQuarterYearRendering(yearQuaterWiseData);
      }
    }
  }, [quarterInfo, selectedUser]);

  const modifyQuarterYearRendering = allQuarterData => {
    // Adding data in list of quarter and year
    const updatedQuarterYearHeaderList = quarterYearHeaderList.map(item => {
      const dataForQuarter = allQuarterData[item.quarterText];

      return {
        ...item,
        data: dataForQuarter,
      };
    });

    setQuarterYearRendering(updatedQuarterYearHeaderList);
  };

  const onDropDownQuarterChanged = (quarter, index) => {
    const modifiedQuarterYearData = quarterYearRendering.map(val => {
      if (val.id === index) {
        return { ...val, quarterText: quarter };
      } else {
        return val;
      }
    });
    setQuarterYearRendering(modifiedQuarterYearData);
    onViewClick(modifiedQuarterYearData[index]);
  };

  const onDropDownYearChanged = (year, index) => {
    const modifiedQuarterYearData = quarterYearRendering.map(val => {
      if (val.id === index) {
        return { ...val, yearText: year };
      } else {
        return val;
      }
    });
    setQuarterYearRendering(modifiedQuarterYearData);
    onViewClick(modifiedQuarterYearData[index]);
  };

  const onDropDownServiceLineChanged = (serviceLine, index) => {
    const modifiedQuarterYearData = quarterYearRendering.map(val => {
      if (val.id === index) {
        return { ...val, serviceLineText: serviceLine };
      } else {
        return val;
      }
    });
    setQuarterYearRendering(modifiedQuarterYearData);
    onViewClick(modifiedQuarterYearData[index]);
  };

  const isFutureQuarter = (quarterSent, yearSent) => {
    const quarterMap = {
      Q1: 1,
      Q2: 2,
      Q3: 3,
      Q4: 4,
    };
    const fiscalYear = parseInt(quarterInfo.fiscal_year, 10);
    const year = parseInt(yearSent, 10);

    if (
      fiscalYear > year ||
      (fiscalYear === year && quarterMap[quarterInfo.quarter] >= quarterMap[quarterSent])
    ) {
      return false;
    } else {
      return true;
    }
  };

  const isCurrentQuarter = (quarterSent, yearSent) => {
    if (quarterInfo.quarter === quarterSent && quarterInfo.fiscal_year === yearSent) {
      return true;
    } else {
      return false;
    }
  };

  const onViewClick = async quarterJsonData => {
    const allQuarterData = await getDashboardSingleQuartersData(
      ROLE_CONST.GCSP,
      currentUser.email,
      quarterJsonData.yearText,
      quarterJsonData.quarterText,
      systemDateTime.split('T')[0],
      currentUser.account_id,
      quarterJsonData.serviceLineText.toLowerCase(),
    );

    const targetQuarterData = allQuarterData[quarterJsonData.quarterText];
    updateQuarterYearData({ ...quarterJsonData, data: targetQuarterData });
  };

  const updateQuarterYearData = updateQuarter => {
    const updatedQuarterYearData = quarterYearRendering.map(val => {
      if (val.id === updateQuarter.id) {
        return { ...updateQuarter };
      } else {
        return val;
      }
    });

    setQuarterYearRendering(updatedQuarterYearData);
  };

  // Helper function to get audit risk value for a specific quarter
  const getAuditRiskValueForQuarter = quarterText => {
    if (
      !completeApiResponse?.auditRiskResultSetQuarterly ||
      !quarterText ||
      currentUser?.role !== 'GCSP'
    ) {
      return 0;
    }

    // Map quarter text to audit risk data format
    const quarterMap = {
      Q1: 'q1',
      Q2: 'q2',
      Q3: 'q3',
      Q4: 'q4',
    };

    const mappedQuarter = quarterMap[quarterText];
    const auditRiskItem = completeApiResponse?.auditRiskResultSetQuarterly.find(
      item => item?.quarter === mappedQuarter,
    );
    return auditRiskItem ? auditRiskItem?.value : 0;
  };

  // Helper function to get enhanced projected forecast value (commit + audit risk) for display
  const getEnhancedForecastForDisplay = (forecastValue, quarterText) => {
    if (currentUser?.role !== 'GCSP') {
      return formatNumberWithKAndM(forecastValue);
    }

    const auditRiskValue = getAuditRiskValueForQuarter(quarterText);
    const enhancedValue = (forecastValue || 0) + auditRiskValue;
    return formatNumberWithKAndM(enhancedValue);
  };

  return (
    <ContainerWrapper>
      {quarterYearRendering !== null
        ? quarterYearRendering.map((item, index) => (
            <QuarterDetailsContainer key={index}>
              <QuarterHeader>
                {/* Title */}
                <CommonSubHeaderContainer>
                  <CommonTitleContainer>
                    <StyledHeader
                      style={
                        isCurrentQuarter(item.quarterText, item.yearText) ||
                        isFutureQuarter(item.quarterText, item.yearText)
                          ? { paddingTop: 20 }
                          : {}
                      }
                    >
                      Quarter {item.quarterText ? `${trimText(item.quarterText)} ` : ''}| FY{' '}
                      {item.yearText}
                      <PillStyle style={{ paddingTop: 4, paddingBottom: 15 }}>
                        {isCurrentQuarter(item.quarterText, item.yearText) ? <CurrentPill /> : null}
                        {isFutureQuarter(item.quarterText, item.yearText) ? (
                          <FuturePillDashboard />
                        ) : null}
                      </PillStyle>
                    </StyledHeader>
                    <InvisibleDiv></InvisibleDiv>
                  </CommonTitleContainer>
                </CommonSubHeaderContainer>
                {/* Quater and Year */}
                <DropDownSubHeaderContainer>
                  <DropDownSelect
                    label="Quarter"
                    options={QUARTER_OPTIONS_LIST}
                    defaultValue={item.quarterText}
                    onDropDownValueChanged={quarter => {
                      onDropDownQuarterChanged(quarter, index, item);
                    }}
                  />
                  <DropDownSelect
                    label="Fiscal Year"
                    options={yearOptionsList}
                    defaultValue={currentYear.toString()}
                    onDropDownValueChanged={year => {
                      onDropDownYearChanged(year, index, item);
                    }}
                  />
                </DropDownSubHeaderContainer>
                {/* Service line and Button */}
                <DropDownButtonSubHeaderContainer>
                  <DropDownSelect
                    label="Service line"
                    options={SERVICE_OPTIONS_LIST}
                    defaultValue={item.serviceLineText}
                    onDropDownValueChanged={services => {
                      onDropDownServiceLineChanged(services, index, item);
                    }}
                  />
                  {!!item?.data && item?.data['quarterlyCalculation'].period !== 'future' ? (
                    <ViewForecastQuarterButton>
                      {item?.data['quarterlyCalculation'].actual_period === 'current' ? (
                        <MotifButton
                          style={buttonStyleForecast}
                          onClick={() => {
                            navigate('/forecast');
                          }}
                        >
                          {BUTTON_TITLE.FORECAST}
                        </MotifButton>
                      ) : item?.data['quarterlyCalculation'].actual_period === 'past' ? (
                        <MotifButton
                          style={buttonStyle}
                          variant="secondary"
                          onClick={() => {
                            navigate('/forecast/past', {
                              state: {
                                year: item.yearText,
                                quarter: item.quarterText,
                                month: 'NA',
                                email: currentUser.email,
                                account: currentUser.account_id,
                                forecast_type: 'quarter',
                              },
                            });
                          }}
                          aria-label={`View details of Quarter ${item.quarterText} FY ${item.yearText}`}
                          disabled={
                            item?.data?.['quarterlyCalculation']?.tag ==
                            MONTH_QUARTER_STATUS.NOT_APPLICABLE
                          }
                        >
                          <span aria-hidden="true">{BUTTON_TITLE.VIEW}</span>
                        </MotifButton>
                      ) : null}
                    </ViewForecastQuarterButton>
                  ) : null}
                </DropDownButtonSubHeaderContainer>
              </QuarterHeader>
              <MonthlyTitleContainer>{QUARTERLY_DETAILS.FORECAST_SUMMARY}</MonthlyTitleContainer>

              {item?.data ? (
                <>
                  {isFutureQuarter(item?.quarterText, item?.yearText) ? (
                    <LegendFutureContainer>
                      <LabelStyle>
                        <NoWrapSpan>
                          {getEnhancedForecastForDisplay(
                            item?.data['quarterlyCalculation'].commit,
                            item?.quarterText,
                          )}{' '}
                        </NoWrapSpan>
                        <RowStyle>
                          <LegendTextStyle>{QUARTERLY_DETAILS.PROJECTED_FORECAST}</LegendTextStyle>
                        </RowStyle>
                      </LabelStyle>
                      <LabelStyle>
                        <NoWrapSpan>
                          {formatNumberWithKAndM(item?.data['quarterlyCalculation'].noCommit)}{' '}
                        </NoWrapSpan>
                        <RowStyle>
                          <LegendTextStyle>{QUARTERLY_DETAILS.UNCOMMITED_PIPELINE}</LegendTextStyle>
                        </RowStyle>
                      </LabelStyle>
                    </LegendFutureContainer>
                  ) : null}

                  {!isFutureQuarter(item?.quarterText, item?.yearText) ? (
                    <div style={{ marginLeft: 20 }}>
                      <Legend
                        quarterlyData={item?.data['quarterlyCalculation']}
                        quarterText={item.quarterText}
                        flagImageSrc={flagIcon}
                        quarterTitle={null}
                        auditRiskData={completeApiResponse?.auditRiskResultSetQuarterly}
                      />
                    </div>
                  ) : null}

                  <MonthlyTitleContainer style={{ backgroundColor: '#F6F6FA', border: '0px' }}>
                    {QUARTERLY_DETAILS.MONTHLY_BREAKDOWN}
                  </MonthlyTitleContainer>
                  <MonthlyDetails
                    quarterData={item}
                    email={currentUser.email}
                    account={currentUser.account_id}
                    auditRiskData={completeApiResponse?.auditRiskResultSetMonthly}
                  />
                </>
              ) : null}
            </QuarterDetailsContainer>
          ))
        : null}
    </ContainerWrapper>
  );
};

export default QuarterDetailsGcsp;
